import {
  ErrorPresentation,
  requiredValidation,
  stringMinMaxValidation,
  stringMaxValidation,
  notEmptyArrayValidation,
  emailValidation,
  oneOfValidation,
  manyOfValidation,
} from 'helpers/validators';
import { Dispatch, SetStateAction } from 'react';
import { EmailValue } from 'components/common/withEmailInput';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { getAvailableFields } from 'shared/domain/visibleField/availableFields';
import {
  BaseReportFormField,
  IssueReportFormField,
  ReportFieldsContent,
} from 'shared/domain/report/types/model/common';
import { FieldSize } from 'shared/types/fieldSize';
import { ValidationTranslationKeys } from 'intl/keys';

type ReportFieldValidator = (value: unknown) => ErrorPresentation;

const noValidation: ReportFieldValidator = () => undefined;

function isEmailValueArray(value: unknown): value is EmailValue[] {
  return Array.isArray(value);
}

// Base validation functions for all reports
export function nameValidation(value: unknown): ErrorPresentation {
  return (
    requiredValidation(value) || stringMinMaxValidation(FieldSize.M, value)
  );
}

export function messageValidation(value: unknown): ErrorPresentation {
  return stringMaxValidation(FieldSize.XL, value);
}

export function recipientsValidation(value: unknown): ErrorPresentation {
  if (!isEmailValueArray(value)) {
    return requiredValidation(value);
  }

  return (
    requiredValidation(value) ||
    notEmptyArrayValidation(value) ||
    (value.some((recipient) => emailValidation(recipient?.email))
      ? { helperText: { id: ValidationTranslationKeys.valueMustBeEmail } }
      : undefined)
  );
}

export function localeCodeValidation(value: unknown): ErrorPresentation {
  return requiredValidation(value);
}

// Extended validation for issue reports (single and multi)
export function fieldsTypeValidation(value: unknown): ErrorPresentation {
  if (typeof value !== 'object' || value === null || !('_id' in value)) {
    return requiredValidation(value);
  }
  return oneOfValidation(
    (value as LabelledEntity)._id,
    Object.values(ReportFieldsContent)
  );
}

export function selectedFieldsValidation(value: unknown): ErrorPresentation {
  if (!Array.isArray(value)) {
    return requiredValidation(value);
  }

  const selectedFields = value as LabelledEntity[];
  return manyOfValidation(
    getAvailableFields().map((field) => field.fieldName),
    selectedFields.map((field) => field._id)
  );
}

type ReportValidationMap = Record<string, ReportFieldValidator>;

// Base validation map for all reports
const baseValidation: ReportValidationMap = {
  recipients: recipientsValidation,
  name: nameValidation,
  message: messageValidation,
  localeCode: localeCodeValidation,
  showPhoneNumbers: noValidation,
  showUserEmails: noValidation,
  showFileNames: noValidation,
  showFileDescriptions: noValidation,
  showActiveLinks: noValidation,
  messagePresentation: noValidation,
  issueAttachments: noValidation,
  linkedDocumentation: noValidation,
  events: noValidation,
  incomingEmailMessages: noValidation,
  workflowDecisions: noValidation,
};

// Extended validation map for issue reports (includes fieldsContent and selectedFields)
const issueReportValidation: ReportValidationMap = {
  ...baseValidation,
  fieldsContent: fieldsTypeValidation,
  selectedFields: selectedFieldsValidation,
};

// Same field validators as issue reports today; inspection-only keys (e.g.
// inspectionInfo) are optional toggles without dedicated validators yet.
const inspectionReportValidation: ReportValidationMap = issueReportValidation;

// Generic validate function
export function validate(
  key: string,
  value: unknown,
  validationMap: ReportValidationMap = baseValidation
): ErrorPresentation {
  if (!validationMap[key]) return;
  return validationMap[key](value);
}

export const BASE_REPORT_DETAILS_STEP_KEYS = [
  BaseReportFormField.recipients,
  BaseReportFormField.name,
  BaseReportFormField.message,
  BaseReportFormField.localeCode,
  BaseReportFormField.messagePresentation,
] as const;

export const ISSUE_REPORT_DETAILS_STEP_KEYS = [
  ...BASE_REPORT_DETAILS_STEP_KEYS,
  IssueReportFormField.fieldsContent,
  IssueReportFormField.selectedFields,
] as const;

export type ReportDetailsStepValidationOptions = {
  keys?: readonly string[];
};

// Generic handleFormValidation function
export function handleFormValidation(
  errorsSetter: Dispatch<SetStateAction<HashMap<ErrorPresentation>>>,
  form: HashMap<unknown>,
  validationMap: ReportValidationMap = baseValidation,
  options?: ReportDetailsStepValidationOptions
): boolean {
  const keysToValidate = options?.keys;
  const entries = keysToValidate
    ? keysToValidate.map((key) => [key, form[key]] as const)
    : Object.entries(form);

  const errors = entries.reduce<HashMap<ErrorPresentation>>(
    (errors, [key, value]) => {
      const error = validate(key, value, validationMap);
      if (error) {
        errors[key] = error;
      }
      return errors;
    },
    {}
  );

  if (keysToValidate) {
    errorsSetter((prev) => {
      const next = { ...prev };
      keysToValidate.forEach((key) => {
        delete next[key];
      });
      return { ...next, ...errors };
    });
  } else {
    errorsSetter(errors);
  }

  return Object.keys(errors).length > 0;
}

// Pre-configured validation functions for issue reports
export function validateIssueReport(
  key: string,
  value: unknown
): ErrorPresentation {
  return validate(key, value, issueReportValidation);
}

export function handleIssueReportFormValidation(
  errorsSetter: Dispatch<SetStateAction<HashMap<ErrorPresentation>>>,
  form: HashMap<unknown>,
  options?: ReportDetailsStepValidationOptions
): boolean {
  return handleFormValidation(errorsSetter, form, issueReportValidation, options);
}

// Pre-configured validation functions for base reports (inspection)
export function validateBaseReport(
  key: string,
  value: unknown
): ErrorPresentation {
  return validate(key, value, baseValidation);
}

export function validateInspectionReport(
  key: string,
  value: unknown
): ErrorPresentation {
  return validate(key, value, inspectionReportValidation);
}

export function handleBaseReportFormValidation(
  errorsSetter: Dispatch<SetStateAction<HashMap<ErrorPresentation>>>,
  form: HashMap<unknown>,
  options?: ReportDetailsStepValidationOptions
): boolean {
  return handleFormValidation(errorsSetter, form, baseValidation, options);
}

export function handleInspectionReportFormValidation(
  errorsSetter: Dispatch<SetStateAction<HashMap<ErrorPresentation>>>,
  form: HashMap<unknown>,
  options?: ReportDetailsStepValidationOptions
): boolean {
  return handleFormValidation(
    errorsSetter,
    form,
    inspectionReportValidation,
    options
  );
}

